/** `ai.diver` — the swooping attacker body. Modes:
 *  - `enter`: an entry swoop from the spawn point (or the top, returning) to
 *    its formation slot — a lerp bent by a sine arc away from the near side;
 *  - `hold`: rides its slot (from the `formation` signal);
 *  - `dive`: on a `sortie` signal (or at once, `launch: immediate`) it falls
 *    at a speed (× a difficulty scalar) weaving on a cosine, armed (dynamic
 *    `armed` tag, for an `armTag` gun) while on screen unless a hold-fire
 *    scalar is set; past the bottom it either returns from the top to its
 *    slot or is removed (`exit`).
 * Behaviour modules steer a dive by INTENT, never by writing the position: a
 * `steer` signal (target x, lateral rate, descent multiplier) replaces the
 * weave for that tick; `sortie-restart` sends the diver back to the top.
 * Emits `dive`; signals `sortie-end` when a dive leaves the bottom. */
import { id, int, num, obj, oneOf } from '../schemaBuilders.js';
import { defineModule, num as read, type ModuleCtx } from '../module.js';
import { clamp } from '../physics.js';
import { addTag, removeTag, type EntityV1 } from '../world.js';
import type { JsonObject } from '../../json.js';

export type DiverConfig = {
  launch: 'formation' | 'immediate';
  entryTicks: number;
  /** Peak sideways bulge of the entry swoop (world units). */
  entryArc: number;
  speed: number;
  /** Descent multiplier scalar (difficulty curve); 1 when unset. */
  speedScalar: string;
  weaveAmp: number;
  /** Weave angular rate (radians per second of dive). */
  weaveRate: number;
  exit: 'return' | 'despawn';
  /** While this scalar is ≥ 1 a dive never arms (a no-fire bonus wave). */
  holdFireScalar: string;
  /** Armed only while the body is this far inside the top and bottom edges. */
  armMargin: number;
};

export const diver = defineModule<DiverConfig>({
  id: 'ai.diver', version: 1, attach: 'entity', role: 'movement',
  summary: 'Swooping attacker: entry swoop to a formation slot, holds it, dives weaving on release (armed on screen), then returns from the top or leaves.',
  config: obj({ launch: oneOf('formation', 'immediate'), entryTicks: int(1, 600), entryArc: num(0, 8), speed: num(0.2, 40), speedScalar: id(),
    weaveAmp: num(0, 12), weaveRate: num(0, 12), exit: oneOf('return', 'despawn'), holdFireScalar: id(), armMargin: num(0, 8) }),
  requires: [], events: ['dive'], presentation: { dive: { cue: 'dash' } },
  proofs: ['entry-swoop-reaches-slot', 'holds-slot', 'dives-on-sortie', 'weave', 'scalar-speed', 'armed-on-screen-only', 'hold-fire', 'returns-from-top',
    'immediate-dive-then-despawn', 'steer-intent', 'restart-from-top'],
  // An immediate diver never holds a slot: it only ever shows its dive.
  animStates: c => (c.launch === 'immediate' ? ['move'] : ['idle', 'move']),
  check(c, context) {
    const errors: string[] = [];
    if (c.launch === 'formation' && !context.globals.includes('ai.formation')) errors.push('a formation launch needs ai.formation');
    if (c.exit === 'return' && c.launch !== 'formation') errors.push('only a formation diver can return to a slot');
    return errors;
  },
  init(ctx, e, params) {
    const dir = params.dir === -1 ? -1 : 1;
    Object.assign(ctx.data(e), { mode: ctx.config.launch === 'immediate' ? 'dive' : 'enter', t: 0, fromX: e.x, fromY: e.y,
      slotX: e.x, slotY: e.y, slotted: false, formed: false, dir, sorties: ctx.config.launch === 'immediate' ? 1 : 0, steer: null, restart: false });
    if (ctx.config.launch === 'immediate') {
      addTag(e, 'diving');
      ctx.emit('dive', { x: e.x, y: e.y - e.h / 2, kind: e.archetype });
    }
  },
  systems: {
    think(ctx, e) {
      if (!e) return;
      const d = ctx.data(e);
      const slot = (ctx.signals('formation')[0]?.data.slots as JsonObject | undefined)?.[e.id];
      if (Array.isArray(slot)) { d.slotX = slot[0] as number; d.slotY = slot[1] as number; d.slotted = true; }
      d.steer = null;
      for (const s of ctx.world.signals) {
        if (s.target !== e.id) continue;
        if (s.kind === 'sortie' && d.mode === 'hold') {
          Object.assign(d, { mode: 'dive', t: 0, dir: s.data.dir === -1 ? -1 : 1, sorties: read(d, 'sorties') + 1 });
          addTag(e, 'diving');
          ctx.emit('dive', { x: e.x, y: e.y - e.h / 2, kind: e.archetype });
        } else if (s.kind === 'steer') d.steer = s.data;
        else if (s.kind === 'sortie-restart' && d.mode === 'dive') d.restart = true;
      }
    },
    move(ctx, e) {
      if (!e || e.status !== 'active') return;
      const d = ctx.data(e);
      if (d.mode === 'enter') enter(ctx, e, d);
      else if (d.mode === 'hold') { e.x = read(d, 'slotX'); e.y = read(d, 'slotY'); }
      else dive(ctx, e, d);
      const h = ctx.world.bounds.h, m = ctx.config.armMargin;
      const armed = d.mode === 'dive' && d.steer === null && e.y - e.h > m && e.y < h - m && ctx.scalar(ctx.config.holdFireScalar, 0) < 1;
      if (armed) addTag(e, 'armed'); else removeTag(e, 'armed');
    },
    present(ctx, e) {
      if (e && e.status === 'active') ctx.setAnim(e, ctx.data(e).mode === 'hold' ? 'idle' : 'move');
    },
  },
});

function enter(ctx: ModuleCtx<DiverConfig>, e: EntityV1, d: JsonObject): void {
  if (d.slotted !== true) return; // waits off-stage until a slot is assigned
  d.t = read(d, 't') + 1;
  const k = Math.min(1, read(d, 't') / ctx.config.entryTicks);
  const fromX = read(d, 'fromX'), side = fromX < ctx.world.bounds.w / 2 ? 1 : -1;
  e.x = fromX + (read(d, 'slotX') - fromX) * k + Math.sin(k * Math.PI) * ctx.config.entryArc * side * (1 - k);
  e.y = read(d, 'fromY') + (read(d, 'slotY') - read(d, 'fromY')) * k;
  if (k >= 1) { d.mode = 'hold'; d.formed = true; }
}

function dive(ctx: ModuleCtx<DiverConfig>, e: EntityV1, d: JsonObject): void {
  const c = ctx.config, w = ctx.world.bounds.w, h = ctx.world.bounds.h;
  if (d.restart === true) { d.restart = false; e.y = 0; }
  d.t = read(d, 't') + 1;
  const steer = d.steer as JsonObject | null;
  const mul = typeof steer?.mul === 'number' ? steer.mul : 1;
  e.vy = c.speed * ctx.scalar(c.speedScalar, 1) * mul;
  e.y += e.vy * ctx.dt;
  if (steer && typeof steer.x === 'number' && typeof steer.rate === 'number') {
    const step = steer.rate * ctx.dt;
    e.vx = clamp(steer.x - e.x, -step, step) / ctx.dt;
  } else e.vx = Math.cos(read(d, 't') * ctx.dt * c.weaveRate) * c.weaveAmp * read(d, 'dir');
  e.x = clamp(e.x + e.vx * ctx.dt, e.w / 2, w - e.w / 2);
  if (e.vx !== 0) e.facing = e.vx > 0 ? 1 : -1;
  if (e.y - e.h <= h + 0.5) return;
  ctx.signal('sortie-end', e, null, { sorties: read(d, 'sorties') });
  removeTag(e, 'diving');
  if (c.exit === 'despawn') { ctx.despawn(e, 'passed'); return; }
  Object.assign(d, { mode: 'enter', t: 0, fromX: e.x, fromY: 0 });
  e.y = 0;
}
